import { Chat, ChatMessage, InventoryItem, Tutor } from "@/components/Types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface ChatSelection {
    messageId?: string | null;
    learningFeedPostId?: string | null;
    paragraphIndex?: number;
    wordIndex?: number;
    word?: string;
    wordsOnLeft?: string[];
    wordsOnRight?: string[];
    phrase?: string;
}

export interface ChatState {
    chatId: string | null;
    agent: Tutor | null;
    currentChat: Chat | null;
    messages: ChatMessage[];
    speechPlaybackRate: number;
    speechPlaybackProgress: number;
    numPlayedMessages: number; // Used for complex workflow tracking for tutorials
    selection: ChatSelection | null;
    isInventoryOpen: boolean;
    dailyLimitReached: boolean;
    newMessageText: string;
    shakeNewMessageInput: boolean;
    environmentTextShown: boolean; // Indicates if the scene text intro finished playing (when available)
    chatToDoItemCompleted: {
        todoItemId: string;
        listTag?: string;
        xpAward?: number;
        xpAwardMultiple?: number;
    } | null;
}

const initialState: ChatState = {
    chatId: null,
    agent: null,
    currentChat: null,
    messages: [],
    speechPlaybackRate: 1.0,
    speechPlaybackProgress: 0,
    numPlayedMessages: 0,
    selection: null,
    isInventoryOpen: false,
    dailyLimitReached: false,
    newMessageText: '',
    shakeNewMessageInput: false,
    environmentTextShown: false,
    chatToDoItemCompleted: null,
}

export const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        setChatId: (state, action: PayloadAction<string>) => {
            state.chatId = action.payload;
        },
        setChatMessages: (state, action: PayloadAction<ChatMessage[]>) => {
            state.messages = action.payload;
        },
        addChatMessage: (state, action: PayloadAction<ChatMessage>) => {
            state.messages = [...state.messages, action.payload];
        },
        setAgent: (state, action: PayloadAction<Tutor>) => {
            state.agent = action.payload;
        },
        setCurrentChat: (state, action: PayloadAction<Chat>) => {
            state.currentChat = action.payload;
        },
        setChatSelection: (state, action: PayloadAction<ChatSelection>) => {
            state.selection = action.payload;
        },
        setSpeechPlaybackProgress: (state, action: PayloadAction<number>) => {
            state.speechPlaybackProgress = action.payload;
        },
        resetChatState: (state) => {
            state.chatId = null;
            state.currentChat = null;
            state.agent = null;
            state.messages = [];
            state.numPlayedMessages = 0;
        },        
        setSpeechPlaybackRate: (state, action: PayloadAction<number>) => {
            state.speechPlaybackRate = action.payload;
        },
        setIsInventoryOpen: (state, action: PayloadAction<boolean>) => {
            state.isInventoryOpen = action.payload;
        },
        setNewMessageText: (state, action: PayloadAction<string>) => {
            state.newMessageText = action.payload;
        },
        setNewMessageTextWithShake: (state, action: PayloadAction<string>) => {
            state.newMessageText = action.payload;
            state.shakeNewMessageInput = true;
        },
        setShakeNewMessageInput: (state, action: PayloadAction<boolean>) => {
            state.shakeNewMessageInput = action.payload;
        },
        setDailyLimitReached: (state, action: PayloadAction<boolean>) => {
            state.dailyLimitReached = action.payload;
        },
        updateChatTaskCompletion: (state, action: PayloadAction<{ task: string, completed: boolean }>) => {
            if (state.currentChat?.chatConfig?.taskConfig?.tasks) {
                let completedFinalTask = false;

                // Get the final task instruction from the chat config
                const completionSteps = state.currentChat.chatConfig.taskConfig.todoItem.chatConfig.completionSteps;

                if (completionSteps) {
                    const finalTask = completionSteps[completionSteps.length - 1];

                    if (finalTask) {
                        completedFinalTask = action.payload.task == finalTask.instruction && action.payload.completed && !state.currentChat.chatConfig.taskConfig.tasks[finalTask.instruction];
                    }
                }

                state.currentChat.chatConfig.taskConfig.tasks[action.payload.task] = action.payload.completed;

                // If the final task is completed, complete all other tasks that are not yet completed
                if (completedFinalTask) {
                    for (const task in state.currentChat.chatConfig.taskConfig.tasks) {
                        if (!state.currentChat.chatConfig.taskConfig.tasks[task]) {
                            state.currentChat.chatConfig.taskConfig.tasks[task] = true;
                        }
                    }

                    state.chatToDoItemCompleted = {
                        todoItemId: state.currentChat.chatConfig.taskConfig.todoItem?._id?.toString() ?? null,
                        xpAwardMultiple: state.currentChat.chatConfig.taskConfig.todoItem?.xpAwardMultiple ?? null,
                        xpAward: state.currentChat.chatConfig.taskConfig.todoItem?.xpAward ?? null,                         
                    }
                }
            }
        },
        setChatToDoItemCompleted: (state, action: PayloadAction<{ todoItemId: string } | null>) => {
            state.chatToDoItemCompleted = action.payload;
        },
        setChatCompleted: (state, action: PayloadAction<boolean>) => {
            state.currentChat.completedAt = action.payload ? (new Date()).toISOString() : null;
        },
        setEnvironmentTextShown: (state, action: PayloadAction<boolean>) => {
            state.environmentTextShown = action.payload;
        },
        incrementNumPlayedMessages: (state) => {
            console.log('incrementNumPlayedMessages', state.numPlayedMessages);
            state.numPlayedMessages++;
        },
    }
});

export const { 
    setChatId, 
    setChatMessages, 
    addChatMessage, 
    setAgent, 
    setCurrentChat, 
    resetChatState, 
    setSpeechPlaybackRate, 
    setSpeechPlaybackProgress, 
    setChatSelection, 
    setIsInventoryOpen, 
    setDailyLimitReached, 
    updateChatTaskCompletion, 
    setNewMessageText,
    setNewMessageTextWithShake,
    setShakeNewMessageInput,
    setChatToDoItemCompleted,
    setChatCompleted,
    setEnvironmentTextShown,
    incrementNumPlayedMessages,
} = chatSlice.actions;
export default chatSlice.reducer;